import { usePage, useForm, router } from '@inertiajs/react';
import { useState, useEffect } from 'react';
import DashboardLayout from '../../../../components/DashboardLayout';
import TipTapEditor from '@/components/editor/TipTapEditor';
import PostPickerModal from '../../../../components/admin/newsletter/PostPickerModal';

interface Newsletter {
  id: number;
  name: string;
  slug: string;
}

interface Issue {
  id?: number;
  subject: string;
  preheader: string;
  status: string;
  issue_number: number | null;
  content: {
    id?: number;
    html_content: string;
    text_content: string;
    ai_markdown?: string;
    tiptap_json?: Record<string, unknown>;
  } | null;
  ai_markdown?: string;
  tiptap_json?: Record<string, unknown>;
}

interface Props {
  newsletter: Newsletter;
  issue: Issue;
  errors?: string[];
  blog_posts: any[];
  [key: string]: any;
}

export default function New() {
  const { newsletter, issue: initialIssue, errors: initialErrors = [], blog_posts = [] } = usePage<Props>().props;
  
  const [showPostPicker, setShowPostPicker] = useState(false);
  const [htmlContent, setHtmlContent] = useState(initialIssue?.content?.html_content || '');
  const [textContent, setTextContent] = useState(initialIssue?.content?.text_content || '');
  const [aiMarkdown, setAiMarkdown] = useState(initialIssue?.content?.ai_markdown || initialIssue?.ai_markdown || '');
  const [tiptapJson, setTiptapJson] = useState<Record<string, unknown>>(initialIssue?.content?.tiptap_json || initialIssue?.tiptap_json || {});
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  
  const { data, setData, post, errors, processing } = useForm({
    subject: initialIssue?.subject || '',
    preheader: initialIssue?.preheader || '',
    content_attributes: {
      id: initialIssue?.content?.id,
      html_content: htmlContent,
      text_content: textContent,
    },
    ai_markdown: aiMarkdown,
    tiptap_json: tiptapJson,
  });

  // Update content when editor changes
  useEffect(() => {
    setData('content_attributes', {
      id: initialIssue?.content?.id,
      html_content: htmlContent,
      text_content: textContent,
    });
  }, [htmlContent, textContent]);

  // Update AI markdown when it changes
  useEffect(() => {
    setData('ai_markdown', aiMarkdown);
  }, [aiMarkdown]);

  // Update tiptap_json when editor changes
  useEffect(() => {
    setData('tiptap_json', tiptapJson);
  }, [tiptapJson]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(`/admin/newsletters/${newsletter.id}/issues`, {
      preserveScroll: true,
    });
  };

  const handleEditorChange = (html: string, json: Record<string, unknown>) => {
    setHtmlContent(html);
    setTiptapJson(json);
    // Extract plain text from HTML for text_content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    setTextContent(plainText);
  };

  const handleAiMarkdownChange = (markdown: string) => {
    setAiMarkdown(markdown);
  };

  const handleInsertPost = (post: { title: string; slug: string }) => {
    // Insert link to post at cursor position in editor
    const linkHtml = `<a href="/blog/${post.slug}">${post.title}</a>`;
    setHtmlContent((prev) => prev + linkHtml);
    setShowPostPicker(false);
  };

  const handlePreview = async () => {
    // For preview, we'll just show the HTML content in a modal
    setPreviewHtml(htmlContent);
    setShowPreview(true);
  };

  const handleSaveDraft = (e: React.MouseEvent) => {
    e.preventDefault();
    // Just submit with draft status (default)
    handleSubmit(e as any);
  };

  return (
    <DashboardLayout>
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <a
          href={`/admin/newsletters/${newsletter.id}/issues`}
          className="text-primary hover:text-primary/80"
        >
          ← Back to Issues
        </a>
      </div>

      <h1 className="text-2xl font-bold text-foreground mb-6">
        Create New Issue - {newsletter.name}
      </h1>

      {initialErrors.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <ul className="list-disc list-inside text-red-600 dark:text-red-400">
            {initialErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Subject */}
        <div className="mb-6">
          <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">
            Subject Line *
          </label>
          <input
            type="text"
            id="subject"
            value={data.subject}
            onChange={(e) => setData('subject', e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-lg focus:ring-primary focus:border-primary bg-background text-foreground"
            placeholder="e.g., Weekly Newsletter - January 15, 2026"
            required
          />
          {errors.subject && (
            <p className="mt-1 text-sm text-red-500 dark:text-red-400">{errors.subject}</p>
          )}
        </div>

        {/* Preheader */}
        <div className="mb-6">
          <label htmlFor="preheader" className="block text-sm font-medium text-foreground mb-2">
            Preview Text
          </label>
          <input
            type="text"
            id="preheader"
            value={data.preheader}
            onChange={(e) => setData('preheader', e.target.value)}
            className="w-full px-3 py-2 border border-input rounded-lg focus:ring-primary focus:border-primary bg-background text-foreground"
            placeholder="A brief preview of your newsletter content..."
            maxLength={150}
          />
          <p className="mt-1 text-sm text-muted-foreground">
            This text appears in email previews (max 150 characters)
          </p>
        </div>

        {/* Content Editor */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-foreground">
              Newsletter Content *
            </label>
            <button
              type="button"
              onClick={() => setShowPostPicker(true)}
              className="px-3 py-1 text-sm bg-primary/10 text-primary rounded hover:bg-primary/20 transition-colors"
            >
              + Insert Blog Post
            </button>
          </div>
          
          <div className="border border-border rounded-lg overflow-hidden">
            <TipTapEditor
              content={htmlContent}
              tiptapJson={tiptapJson}
              onChange={handleEditorChange}
              editable={true}
              enableAiMarkdown={true}
              aiMarkdown={aiMarkdown}
              onAiMarkdownChange={handleAiMarkdownChange}
            />
          </div>
          {errors['content_attributes.html_content'] && (
            <p className="mt-1 text-sm text-red-500 dark:text-red-400">{errors['content_attributes.html_content']}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-end">
          <button
            type="button"
            onClick={() => setShowPostPicker(true)}
            className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
          >
            Insert Post
          </button>
          
          <button
            type="button"
            onClick={handlePreview}
            className="px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors disabled:opacity-50"
            disabled={!htmlContent}
          >
            Preview
          </button>
          
          <button
            type="submit"
            disabled={processing}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {processing ? 'Saving...' : 'Save Draft'}
          </button>
        </div>
      </form>

      {/* Post Picker Modal */}
      {showPostPicker && (
        <PostPickerModal
          onClose={() => setShowPostPicker(false)}
          onSelect={handleInsertPost}
        />
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500/75 dark:bg-black/75" onClick={() => setShowPreview(false)}></div>
            </div>

            <div className="inline-block align-bottom bg-background dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle max-w-2xl w-full">
              <div className="bg-background dark:bg-gray-900 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-foreground mb-4">
                      Email Preview
                    </h3>
                    <div 
                      className="prose max-w-none p-4 border border-border rounded-lg bg-muted"
                      dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                  </div>
                </div>
              </div>
              <div className="bg-muted dark:bg-gray-800 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setShowPreview(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </DashboardLayout>
  );
}
